import { useEffect, useState } from 'react';

export interface LoadImageProps {
  src?: string;
  srcSet?: string;
}

interface Result {
  imgProps: LoadImageProps;
  loading: boolean;
  finished: boolean;
  error: boolean;
}

const DELAY_LOADING_STATE = 200;

export function useLoadImageInMemory(props: LoadImageProps, disabled = false): Result {
  const [result, setResult] = useState<Result>({
    imgProps: { src: undefined, srcSet: undefined },
    loading: false,
    finished: false,
    error: false
  });

  useEffect(() => {
    async function loadImage(src?: string, srcSet?: string) {
      if (!src && !srcSet) {
        setResult((prev) => ({
          ...prev,
          finished: true,
          loading: false
        }));
        return;
      }

      // not loading yet to hide animations for quick requests
      setResult((prev) => ({
        ...prev,
        finished: false,
        loading: false
      }));

      // delay loading state to skip that for quick calls
      setTimeout(() => {
        setResult((prev) => {
          if (prev.finished) {
            return prev;
          }
          return {
            ...prev,
            loading: true
          };
        });
      }, DELAY_LOADING_STATE);

      if (await loadImageInMemory(src, srcSet)) {
        setResult({
          imgProps: {
            src,
            srcSet
          },
          loading: false,
          error: false,
          finished: true
        });
      } else {
        setResult((prev) => ({
          ...prev,
          loading: false,
          error: true,
          finished: true
        }));
      }
    }

    if (!disabled) {
      loadImage(props.src, props.srcSet);
    }
  }, [props.src, props.srcSet, disabled]);

  return result;
}

async function loadImageInMemory(src?: string, srcSet?: string): Promise<boolean> {
  let resolver: (el: boolean) => void;
  const result = new Promise<boolean>((resolve) => {
    resolver = resolve;
  });

  const image = new Image();
  if (srcSet) {
    image.srcset = srcSet;
  }
  if (src) {
    image.src = src;
  }
  image.onload = () => {
    resolver(true);
  };
  image.onerror = () => {
    resolver(false);
  };

  return result;
}
